import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives, Fragment as _Fragment } from "vue";
const _hoisted_1 = ["draggable"];
const _hoisted_2 = {
  class: "flex h-full flex-col justify-start gap-1.5"
};
const _hoisted_3 = {
  key: 0,
  class: "inline-flex w-fit rounded-full status-badge is-info px-2 py-0.5 text-[10px] font-medium"
};
const _hoisted_4 = {
  class: "break-words text-sm font-medium leading-5 text-slate-900"
};
const _hoisted_5 = {
  class: "flex items-baseline gap-1.5"
};
const _hoisted_6 = {
  class: "text-sm font-semibold leading-4 text-slate-900"
};
const _hoisted_7 = {
  class: "flex items-center gap-1.5 text-[11px] leading-4 text-slate-500"
};
const _hoisted_8 = {
  class: "font-medium text-slate-700"
};
const _hoisted_9 = {
  key: 0,
  class: "status-badge is-warning"
};
const _hoisted_10 = {
  class: "space-y-0.5 text-[11px] leading-4 text-slate-500"
};
const _hoisted_11 = {
  class: "truncate"
};
const _hoisted_12 = {
  class: "ml-1 text-slate-600"
};
const _hoisted_13 = {
  class: "truncate"
};
const _hoisted_14 = {
  class: "ml-1 text-slate-600"
};
const _hoisted_15 = {
  class: "flex flex-wrap gap-1 text-[10px]"
};
const _hoisted_16 = ["title"];
const _hoisted_17 = ["title"];
const _hoisted_18 = {
  class: "absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100"
};
const _hoisted_19 = ["disabled"];
const _hoisted_20 = ["disabled"];
const _hoisted_21 = ["disabled"];
import { computed, ref } from 'vue';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { displayInvoiceCompanyName, formatProfitRate, formatWanAmount, isFullProfitRateCustomer } from '../utils';
import { downstreamAllocationSummary, downstreamPreferenceSummary } from '../downstreamRules.mjs';
import { formatProductQuotaSummary } from '../productQuota';
export default {
  __name: 'InvoiceMatrixCustomerCell',
  props: {
    region: {
      type: Object,
      required: true
    },
    regions: {
      type: Array,
      default: () => []
    },
    customer: {
      type: Object,
      default: null
    },
    rowIndex: {
      type: Number,
      default: null
    },
    draggingCustomerId: {
      type: [Number, String],
      default: null
    },
    downstreamAllocations: {
      type: Array,
      default: () => []
    },
    downstreamTagPreferences: {
      type: Array,
      default: () => []
    },
    syncing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['create-customer', 'edit-customer', 'delete', 'drag-start', 'drag-over', 'drag-leave', 'drop-customer', 'drag-end'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const dropPosition = ref(null);
    const companyName = computed(() => displayInvoiceCompanyName(props.customer));
    const isHundredPercentCustomer = computed(() => isFullProfitRateCustomer(props.customer));
    const canDrag = computed(() => Boolean(props.customer?.id) && !props.syncing);
    const isDraggingSource = computed(() => props.customer?.id != null && String(props.customer.id) === String(props.draggingCustomerId));
    const isDropTarget = computed(() => Boolean(dropPosition.value) && !isDraggingSource.value);
    const allocationSummary = computed(() => downstreamAllocationSummary(props.downstreamAllocations, props.customer));
    const preferenceSummary = computed(() => downstreamPreferenceSummary(props.downstreamTagPreferences, props.customer?.id));
    const dropHint = computed(() => {
      if (!dropPosition.value || isDraggingSource.value) return '';
      if (dropPosition.value === 'before') return '插入到此客户前';
      if (dropPosition.value === 'after') return '插入到此客户后';
      return '拖放到此区域';
    });
    const dropHintClass = computed(() => ['pointer-events-none absolute left-2 right-2 z-20 rounded-md border px-2 py-1 text-center text-[11px] font-medium shadow-sm', dropPosition.value === 'after' ? 'bottom-2' : 'top-2', 'border-blue-200 bg-blue-50 text-blue-700']);
    const cellClass = computed(() => ['group relative min-h-[112px] border-r border-b border-slate-200 bg-white p-2.5 align-top transition-colors duration-150', isHundredPercentCustomer.value ? 'border-l-2 border-l-amber-400 bg-amber-50/30' : '', props.customer ? 'cursor-default hover:bg-slate-50' : 'cursor-pointer', canDrag.value ? 'cursor-move' : '', isDraggingSource.value ? 'opacity-55 ring-1 ring-inset ring-blue-200 bg-slate-50' : '', isDropTarget.value ? 'bg-blue-50/60 ring-1 ring-inset ring-blue-300' : '', dropPosition.value === 'before' && !isDraggingSource.value ? 'before:absolute before:left-1 before:right-1 before:top-0 before:z-10 before:h-1 before:rounded-full before:bg-blue-500' : '', dropPosition.value === 'after' && !isDraggingSource.value ? 'after:absolute after:left-1 after:right-1 after:bottom-0 after:z-10 after:h-1 after:rounded-full after:bg-blue-500' : '', dropPosition.value === 'empty' && !isDraggingSource.value ? 'ring-2 ring-inset ring-blue-300 bg-blue-50/70' : '']);
    function activate() {
      if (!props.customer || props.syncing) return;
      emit('edit-customer', props.customer);
    }
    function handleDragStart(event) {
      if (!canDrag.value) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(props.customer.id));
      emit('drag-start', {
        customer: props.customer,
        planCustomerId: props.customer.id,
        sourceRegionId: props.customer.regionBucketId
      });
    }
    function handleDragOver(event) {
      if (props.syncing) return;
      const position = dragPosition(event);
      dropPosition.value = position;
      event.dataTransfer.dropEffect = 'move';
      emit('drag-over', {
        region: props.region,
        customer: props.customer,
        position,
        rowIndex: props.rowIndex
      });
    }
    function handleDragLeave() {
      dropPosition.value = null;
      emit('drag-leave');
    }
    function handleDrop(event) {
      if (props.syncing) return;
      const position = dragPosition(event);
      dropPosition.value = null;
      emit('drop-customer', {
        region: props.region,
        customer: props.customer,
        position,
        rowIndex: props.rowIndex
      });
    }
    function handleDragEnd() {
      dropPosition.value = null;
      emit('drag-end');
    }
    function dragPosition(event) {
      if (!props.customer) return 'empty';
      const rect = event.currentTarget.getBoundingClientRect();
      return event.clientY - rect.top < rect.height / 2 ? 'before' : 'after';
    }
    return (_ctx, _cache) => {
      const _directive_permission = _resolveDirective("permission");
      return _openBlock(), _createElementBlock("div", {
        class: _normalizeClass(cellClass.value),
        draggable: canDrag.value,
        onClick: activate,
        onDragstart: handleDragStart,
        onDragover: _withModifiers(handleDragOver, ["prevent"]),
        onDragleave: handleDragLeave,
        onDrop: _withModifiers(handleDrop, ["prevent"]),
        onDragend: handleDragEnd
      }, [dropHint.value ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(dropHintClass.value)
      }, _toDisplayString(dropHint.value), 3)) : _createCommentVNode("", true), __props.customer ? (_openBlock(), _createElementBlock(_Fragment, {
        key: 1
      }, [_createElementVNode("div", _hoisted_2, [isDraggingSource.value ? (_openBlock(), _createElementBlock("div", _hoisted_3, " 拖动中 ")) : _createCommentVNode("", true), _createElementVNode("div", _hoisted_4, _toDisplayString(companyName.value), 1), _createElementVNode("div", _hoisted_5, [_cache[3] || (_cache[3] = _createElementVNode("span", {
        class: "text-[10px] uppercase tracking-wide text-slate-400"
      }, "额度", -1)), _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(formatWanAmount)(__props.customer.targetOutputAmount)), 1)]), _createElementVNode("div", _hoisted_7, [_cache[4] || (_cache[4] = _createElementVNode("span", {
        class: "text-[10px] uppercase tracking-wide text-slate-400"
      }, "利率", -1)), _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(formatProfitRate)(__props.customer.targetProfitRate)), 1), _unref(isFullProfitRateCustomer)(__props.customer) ? (_openBlock(), _createElementBlock("span", _hoisted_9, "100%")) : _createCommentVNode("", true)]), _createElementVNode("div", _hoisted_10, [_createElementVNode("div", _hoisted_11, [_cache[5] || (_cache[5] = _createElementVNode("span", {
        class: "text-[10px] uppercase tracking-wide text-slate-400"
      }, "进项", -1)), _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(formatProductQuotaSummary)(__props.customer.inputProductQuotas).shortText), 1)]), _createElementVNode("div", _hoisted_13, [_cache[6] || (_cache[6] = _createElementVNode("span", {
        class: "text-[10px] uppercase tracking-wide text-slate-400"
      }, "销项", -1)), _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(formatProductQuotaSummary)(__props.customer.outputProductQuotas).shortText), 1)])]), _createElementVNode("div", _hoisted_15, [allocationSummary.value.enabledCount > 0 ? (_openBlock(), _createElementBlock("span", {
        key: 0,
        class: "rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700",
        title: `已指定 ${_unref(formatWanAmount)(allocationSummary.value.totalAmount)}`
      }, " 下游 " + _toDisplayString(allocationSummary.value.enabledCount), 9, _hoisted_16)) : _createCommentVNode("", true), preferenceSummary.value.enabledCount > 0 ? (_openBlock(), _createElementBlock("span", {
        key: 1,
        class: "rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700",
        title: preferenceSummary.value.shortText
      }, " 偏好 " + _toDisplayString(preferenceSummary.value.enabledCount), 9, _hoisted_17)) : _createCommentVNode("", true)])]), _createElementVNode("div", _hoisted_18, [_withDirectives((_openBlock(), _createElementBlock("button", {
        type: "button",
        class: "table-action-button is-primary",
        title: "编辑客户",
        "aria-label": "编辑客户",
        disabled: __props.syncing,
        onClick: _cache[0] || (_cache[0] = _withModifiers($event => emit('edit-customer', __props.customer), ["stop"]))
      }, [_createVNode(_unref(PencilSquareIcon), {
        class: "h-3.5 w-3.5"
      })], 8, _hoisted_19)), [[_directive_permission, 'enterprise-invoice:plan-customer:manage']]), _withDirectives((_openBlock(), _createElementBlock("button", {
        type: "button",
        class: "table-action-button is-danger",
        title: "删除客户",
        "aria-label": "删除客户",
        disabled: __props.syncing,
        onClick: _cache[1] || (_cache[1] = _withModifiers($event => emit('delete', __props.customer), ["stop"]))
      }, [_createVNode(_unref(TrashIcon), {
        class: "h-3.5 w-3.5"
      })], 8, _hoisted_20)), [[_directive_permission, 'enterprise-invoice:plan-customer:manage']])])], 64)) : _withDirectives((_openBlock(), _createElementBlock("button", {
        key: 2,
        type: "button",
        class: "flex h-full min-h-[112px] w-full flex-col items-center justify-center gap-1 border border-dashed border-slate-200 text-slate-400 transition-colors hover:border-blue-300 hover:bg-blue-50/40 hover:text-blue-500",
        disabled: __props.syncing,
        onClick: _cache[2] || (_cache[2] = _withModifiers($event => emit('create-customer', {
          region: __props.region
        }), ["stop"]))
      }, [_createVNode(_unref(PlusIcon), {
        class: "h-4 w-4"
      }), _cache[7] || (_cache[7] = _createElementVNode("span", {
        class: "text-[11px] font-medium"
      }, "新增客户", -1))], 8, _hoisted_21)), [[_directive_permission, 'enterprise-invoice:plan-customer:manage']])], 42, _hoisted_1);
    };
  }
};